// ─────────────────────────────────────────────────────────────────────────────
// edgeSwipeManager — gesto global de "swipe to go back" (edge swipe) para overlays
//
// Mantém uma PILHA de overlays abertos (Offcanvas/Modal). Um único conjunto de
// listeners de toque é instalado no documento enquanto houver algo na pilha.
// Ao detectar um swipe a partir da borda da tela, apenas o overlay do TOPO da
// pilha é fechado — telas empilhadas fecham uma de cada vez, na ordem certa.
//
// Compartilhado pelo componente <SwipeableOffcanvas>, mas pode ser usado direto
// por qualquer overlay que precise do gesto.
// ─────────────────────────────────────────────────────────────────────────────

const EDGE = 24; // px a partir da borda p/ considerar início do gesto
const MIN_DIST = 60; // distância horizontal mínima do swipe (px)
const MAX_Y = 70; // tolerância vertical (px)

/* Deslocamento a partir do qual o toque deixou de ser um TOQUE e virou um
   arraste. Abaixo disto nada é cancelado — ver `onTouchMove`. */
const FOLGA_TOQUE = 10;

let stack = []; // [{ id, onClose, side }] — último = topo (z-index mais alto)
let listening = false;

// estado do gesto em andamento
let startedOnEdge = false;
let edgeSide = null; // "left" | "right" | null
let startX = null;
let startY = null;

function resetGesture() {
  startedOnEdge = false;
  edgeSide = null;
  startX = null;
  startY = null;
}

function topItem() {
  return stack.length ? stack[stack.length - 1] : null;
}

function sideAllows(itemSide, detected) {
  return itemSide === "both" || itemSide === detected;
}

function onTouchStart(e) {
  const top = topItem();
  if (!top) {
    resetGesture();
    return;
  }
  const t = e.touches && e.touches[0];
  if (!t) return;

  startX = t.clientX;
  startY = t.clientY;

  const vw = window.innerWidth;
  let detected = null;
  if (startX <= EDGE) detected = "left";
  else if (startX >= vw - EDGE) detected = "right";

  const matches = !!detected && sideAllows(top.side, detected);
  startedOnEdge = matches;
  edgeSide = matches ? detected : null;
}

function onTouchMove(e) {
  // Começou na borda → evita o iOS interpretar como back navigation nativo.
  if (!startedOnEdge) return;

  // ── MAS SÓ DEPOIS QUE VIRA ARRASTE ─────────────────────────────────────────
  //
  // `preventDefault()` num `touchmove` cancela o clique sintético que viria
  // depois — e, com ele, o foco e o teclado. Cancelando ao PRIMEIRO pixel de
  // movimento, todo toque com um tremidinho de dedo a menos de 24 px da borda
  // da tela morria: a pessoa tocava o campo e não acontecia nada, e no toque
  // seguinte (mais firme) funcionava. Campo de formulário encosta na borda com
  // facilidade — no check-in de treino os campos do passo 1 vão de ponta a
  // ponta.
  //
  // O gesto de voltar só precisa ser defendido do iOS quando já é um arraste de
  // verdade. Até `FOLGA_TOQUE`, o toque segue sendo um toque.
  const t = e.touches && e.touches[0];
  if (!t || startX == null || startY == null) return;
  const dx = Math.abs(t.clientX - startX);
  const dy = Math.abs(t.clientY - startY);
  if (dx < FOLGA_TOQUE || dx <= dy) return;

  e.preventDefault();
}

function onTouchEnd(e) {
  if (!startedOnEdge) {
    resetGesture();
    return;
  }
  const t = e.changedTouches && e.changedTouches[0];
  const top = topItem();

  if (t && top && startX != null && startY != null && edgeSide) {
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    const horizontal = Math.abs(dy) <= MAX_Y;

    const leftClose = edgeSide === "left" && dx >= MIN_DIST && horizontal;
    const rightClose = edgeSide === "right" && dx <= -MIN_DIST && horizontal;

    if (leftClose || rightClose) {
      try {
        top.onClose && top.onClose();
      } catch (_) {
        // ignora erros do handler de fechamento
      }
    }
  }
  resetGesture();
}

function startListening() {
  if (listening) return;
  document.addEventListener("touchstart", onTouchStart, { passive: true });
  document.addEventListener("touchmove", onTouchMove, { passive: false });
  document.addEventListener("touchend", onTouchEnd, { passive: true });
  listening = true;
}

function stopListeningIfIdle() {
  if (!listening || stack.length > 0) return;
  document.removeEventListener("touchstart", onTouchStart);
  document.removeEventListener("touchmove", onTouchMove);
  document.removeEventListener("touchend", onTouchEnd);
  listening = false;
}

let counter = 0;

/**
 * Registra um overlay na pilha de swipe-back.
 * @param {Function} onClose - chamado quando o gesto fecha ESTE overlay (se estiver no topo).
 * @param {"left"|"right"|"both"} [side="both"]
 * @returns {number} id usado para atualizar/remover o registro.
 */
export function pushEdgeSwipe(onClose, side = "both") {
  const id = ++counter;
  stack.push({ id, onClose, side });
  startListening();
  return id;
}

/** Atualiza o handler/lado de um registro existente (sem mudar a ordem na pilha). */
export function updateEdgeSwipe(id, onClose, side) {
  const item = stack.find((s) => s.id === id);
  if (!item) return;
  if (typeof onClose === "function") item.onClose = onClose;
  if (side) item.side = side;
}

/** Remove um overlay da pilha. */
export function popEdgeSwipe(id) {
  stack = stack.filter((s) => s.id !== id);
  stopListeningIfIdle();
}
